import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Popper, CircularProgress, ClickAwayListener } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useThemeContext } from "../../../Context/ThemeContext";
import { useTranslation } from "react-i18next";
import { DARKCOLORS, LIGHTCOLORS } from "../../../Utils/constants";
import { getSettings, updateSettings } from "../../../Services/authService";
import SuccessNotification from "../../../Components/Common/SuccessNotification";

const SettingsModal = ({ open, anchorEl, onClose, onLowTriggerPointChange }) => {
  const { mode } = useThemeContext();
  const { t } = useTranslation();

  const [settings, setSettings] = useState({ lowBalanceTrigger: "", inactiveDaysThreshold: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const cardBgColor = mode === "dark" ? DARKCOLORS.BackgroundMenu : LIGHTCOLORS.BackgroundMenu;
  const textColor = mode === "dark" ? DARKCOLORS.BodyLightBackground : LIGHTCOLORS.BodyLightBackground;
  const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
  const errorTextColor = mode === 'dark' ? DARKCOLORS.Danger500 : LIGHTCOLORS.Danger500;

  const popupStyle = {
    mt: 1,
    p: 2,
    width: "281px",
    borderRadius: "12px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
    backgroundColor: cardBgColor,
    zIndex: 3000,
  };

  // const overlayStyle = {
  //   position: "fixed",
  //   top: 0,
  //   left: 0,
  //   width: "100%",
  //   height: "100%",
  //   backgroundColor: "rgba(0, 0, 0, 0.5)", // Semi-transparent black
  //   backdropFilter: "blur(5px)", // Blur effect
  //   zIndex: 1500, // Place it behind the popup
  // };

  const inputStyle = {
    borderRadius: "8px",
    backgroundColor: "#DDD8F8",
    padding: "5px 6px 4px 9px",
    fontSize: "13px",
    fontWeight: "bold",
    color: LIGHTCOLORS.BodyLightBackground,
    border: "1px solid #E0E0E0",
    width: "45px",
    textAlign: "center",
  };
  // Fetch settings from the backend when the modal opens
  useEffect(() => {
    // console.log(t('membershipCards.debugNotificationState'), openNotification);

    if (open) {
      setLoading(true);
      getSettings()
        .then((data) => {
          //  console.log("Returned data:", data);
          setSettings({
            lowBalanceTrigger: data.lowBalanceTrigger || "25",
            inactiveDaysThreshold: data.inactiveDaysThreshold || "120",
          });
          onLowTriggerPointChange(data.lowBalanceTrigger || '25')
        })
        .catch((error) => {
          console.error(t("membershipCards.errorFetchingSettings"), error);
        })
        .finally(() => setLoading(false));
    }
  }, [open, onLowTriggerPointChange]);

  // Handle save button click
  const handleSave = async () => {
    const zeroField = Object.keys(settings).find(
      (f) => settings[f] !== "" && Number(settings[f]) === 0,
    );
    if (zeroField) {
      setFieldErrors((prev) => ({
        ...prev,
        [zeroField]: t("membershipCards.zeroNotAllowed"),
      }));
      return;
    }
    setSaving(true);
    try {
      await updateSettings(settings);
      onLowTriggerPointChange(settings.lowBalanceTrigger); // Notify parent of the new low trigger point

      // Reset the notification state to force re-render
      setOpenNotification(true);
    } catch (error) {
      console.error(t("membershipCards.errorUpdatingSettings"), error);
    } finally {
      setSaving(false);
      // Delay closing the modal to allow notification to display
      setTimeout(() => {
        onClose();
        setOpenNotification(false);
      }, 1500);
    }
  };

  const handleClickAway = () => {
    // Prevent popup from closing when notification is visible
    if (!openNotification) {
      onClose();
    }
  };
  // Handle input value change
  const handleInputChange = (field, value) => {
    // Allow only positive numbers with up to 2 decimal places (e.g. "1.11")
    if (value !== "" && !/^\d*\.?\d{0,2}$/.test(value)) return;
    setSettings((prevSettings) => ({ ...prevSettings, [field]: value }));
    setFieldErrors((prev) => ({
      ...prev,
      [field]:
        value !== "" && Number(value) === 0
          ? t("membershipCards.zeroNotAllowed")
          : "",
    }));
  };

  if (!open) return null;

  return (
    <>
      {/* Blurred Background Overlay */}
      {/* <Portal>
        {open && <Box sx={overlayStyle} />}
      </Portal> */}

      <ClickAwayListener onClickAway={handleClickAway}>
        <Popper open={open} anchorEl={anchorEl} placement="bottom-start" disablePortal>
          <Box sx={popupStyle}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "16px", color: textColor }}>
                {t("membershipCards.settings")}
              </Typography>
              <IconButton size="small" onClick={onClose}>
                <CloseIcon style={{ color: textColor }} />
              </IconButton>
            </Box>

            {/* Content */}
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", color: textColor }}>
                    {t("membershipCards.lowTriggerPoint")}
                  </Typography>
                  <Box
                    component="input"
                    sx={inputStyle}
                    value={settings.lowBalanceTrigger}
                    onChange={(e) => handleInputChange("lowBalanceTrigger", e.target.value)}
                  />
                </Box>
                {fieldErrors.lowBalanceTrigger && (
                  <Typography
                    sx={{
                      color: errorTextColor,
                      fontSize: "12px",
                      mt: -1,
                      mb: 1,
                      textAlign: "right",
                    }}
                  >
                    {fieldErrors.lowBalanceTrigger}
                  </Typography>
                )}

                {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: "500", color: textColor }}>
                    {t("membershipCards.inactiveDays")}
                  </Typography>
                  <Box
                    component="input"
                    sx={inputStyle}
                    value={settings.inactiveDaysThreshold}
                    onChange={(e) => handleInputChange("inactiveDaysThreshold", e.target.value)}
                  />
                </Box> */}

                {/* Save Button */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                  <button type="button" style={{ background: btnColor, padding: "10px 13px", fontSize: "13px" }} className='save-btn' onClick={handleSave}>
                    {saving ? <CircularProgress size={18} color="inherit" /> : t("membershipCards.save")}
                  </button>
                </Box>
              </>
            )}
          </Box>
        </Popper>
      </ClickAwayListener>
      {/* Success Notification */}
      <SuccessNotification
        open={openNotification}
        message={t('membershipCards.successMessage')}
        severity="success"
        onClose={() => setOpenNotification(false)}
      />
    </>
  );
};

export default SettingsModal;
